export const M8_READING_PRACTICE = {
  id: "m8-reading-practice",
  title: "Module 8: Reading Practice",
  sets: [

    // SET 1: Travel Blog
    {
      id: "m8-reading-s1",
      title: "Reading 1: Travel Adventures",
      category: "reading",
      questions: [
        {
          type: "passage",
          passage: `**My Travel Blog - Entry 1**\n\nI've always loved traveling, and this year has been incredible! I've already visited five countries since January. My most amazing experience was in Peru. I've climbed Machu Picchu and I've tried traditional Peruvian food. It was absolutely delicious!`
        },
        {
          type: "mc",
          q: "How many countries has the blogger visited this year?",
          options: ["Five", "Three", "Ten"],
          a: "Five",
          tip: "Text says 'five countries since January'"
        },
        {
          type: "input",
          q: `The blogger has ___ visited five countries. (modifier showing completion)`,
          a: "already",
          tip: "Already = completed, possibly earlier than expected"
        },
        {
          type: "mc",
          q: "What has the blogger done in Peru?",
          options: ["Climbed Machu Picchu and tried food", "Only climbed", "Only ate food"],
          a: "Climbed Machu Picchu and tried food",
          tip: "Both activities mentioned in text"
        },
        {
          type: "passage",
          passage: `**Entry 2 - Current Trip**\n\nRight now, I'm in Thailand and I've been exploring temples for three days. I still haven't visited the Grand Palace yet, but I'm going there tomorrow. I've just arrived at a beautiful beach in Phuket. The weather is perfect!`
        },
        {
          type: "mc",
          q: "How long has the blogger been exploring temples?",
          options: ["Three days", "One day", "One week"],
          a: "Three days",
          tip: "Text says 'for three days'"
        },
        {
          type: "input",
          q: `The blogger ___ hasn't visited the Grand Palace. (shows ongoing situation)`,
          a: "still",
          tip: "Still = expected action hasn't happened yet"
        },
        {
          type: "order",
          q: "Time sequence: 'I've ___ arrived' (very recent)",
          words: ["just"],
          a: "'I've just arrived'",
          tip: "Just = very recently, short time ago"
        },
        {
          type: "passage",
          passage: `**Entry 3 - Reflections**\n\nI've learned so much from traveling. I've made myself more independent by planning trips myself. I've taught myself basic phrases in five languages. These experiences have definitely changed my perspective on life. I haven't decided where to go next yet!`
        },
        {
          type: "mc",
          q: "The blogger has made ___ more independent.",
          options: ["himself/herself", "his friends", "other people"],
          a: "himself/herself",
          tip: "Reflexive pronoun - subject affects themselves"
        },
        {
          type: "input",
          q: `The blogger has taught ___ languages. (reflexive)`,
          a: "himself/herself",
          tip: "Taught himself/herself = learned alone"
        },
        {
          type: "mc",
          q: "Has the blogger decided the next destination?",
          options: ["No, not yet", "Yes, Thailand", "Yes, Peru"],
          a: "No, not yet",
          tip: "'Haven't decided...yet' = hasn't happened"
        },
        {
          type: "passage",
          passage: `**Entry 4 - Future Plans**\n\nI'm definitely going to continue traveling next year. I think I'll probably visit South America again. I might go to Argentina, but I haven't decided for sure. I'm going to save money for the trip starting next month.`
        },
        {
          type: "order",
          q: "Certainty levels: 'I'm ___ going to continue' (100%)",
          words: ["definitely"],
          a: "'I'm definitely going to continue'",
          tip: "Definitely = 100% certain"
        },
        {
          type: "mc",
          q: "Argentina trip certainty level?",
          options: ["Might go - 50% possible", "Will definitely go - 100%", "Won't go - 0%"],
          a: "Might go - 50% possible",
          tip: "Might = uncertain possibility"
        }
      ]
    },

    // SET 2: Home Improvement Diary
    {
      id: "m8-reading-s2",
      title: "Reading 2: DIY Home Projects",
      category: "reading",
      questions: [
        {
          type: "passage",
          passage: `**Week 1 - Getting Started**\n\nWe've been renovating our house for two weeks now. It's exhausting but exciting! We've already painted the living room ourselves. I've been learning carpentry by watching online videos. My husband has been fixing the broken tiles in the bathroom since Monday.`
        },
        {
          type: "mc",
          q: "How long have they been renovating?",
          options: ["Two weeks", "One week", "Two months"],
          a: "Two weeks",
          tip: "Text says 'for two weeks now'"
        },
        {
          type: "input",
          q: `They painted ___ (reflexive - they did it, not professionals)`,
          a: "themselves",
          tip: "Themselves = they did the action to their own house"
        },
        {
          type: "mc",
          q: "The author has been learning carpentry by ___.",
          options: ["watching videos", "taking classes", "hiring professionals"],
          a: "watching videos",
          tip: "Text mentions 'watching online videos'"
        },
        {
          type: "passage",
          passage: `**Week 2 - Progress Update**\n\nThe house looks much better already! I've just finished installing new shelves. We still haven't replaced the old windows yet because it's too expensive. My daughter has been helping us paint her bedroom. She's been choosing the colors herself!`
        },
        {
          type: "order",
          q: "Recent completion: 'I've ___ finished installing shelves'",
          words: ["just"],
          a: "'I've just finished'",
          tip: "Just = very recently completed"
        },
        {
          type: "mc",
          q: "Why haven't they replaced windows?",
          options: ["Too expensive", "Not broken", "Don't want to"],
          a: "Too expensive",
          tip: "Text explicitly states 'too expensive'"
        },
        {
          type: "input",
          q: `Daughter choosing colors ___ (reflexive - she's choosing alone)`,
          a: "herself",
          tip: "Herself = without help from others"
        },
        {
          type: "passage",
          passage: `**Week 3 - Challenges**\n\nRenovating is harder than we thought! It's almost impossible to find time for everything. The work has been very disorganized because we're inexperienced. Some tasks are still incomplete, but we're making progress. It's been incredibly tiring!`
        },
        {
          type: "mc",
          q: "'Impossible' and 'disorganized' are examples of:",
          options: ["Negative prefixes (im-, dis-)", "Positive adjectives", "Verb tenses"],
          a: "Negative prefixes (im-, dis-)",
          tip: "Im- and dis- make words negative"
        },
        {
          type: "input",
          q: `'Incomplete' means not ___. (negative prefix meaning)`,
          a: "complete",
          tip: "In- prefix makes 'complete' negative"
        },
        {
          type: "mc",
          q: "What's the main problem they're facing?",
          options: ["Lack of experience and organization", "Not enough money", "Bad weather"],
          a: "Lack of experience and organization",
          tip: "'Inexperienced' and 'disorganized' mentioned"
        },
        {
          type: "passage",
          passage: `**Week 4 - Future Plans**\n\nWe're definitely going to hire professionals for the kitchen next month. I think we'll probably finish the bedrooms ourselves. We might repaint the exterior walls, but we're not sure yet. It will definitely be worth it when everything is done!`
        },
        {
          type: "order",
          q: "Professional help: 'We're ___ going to hire' (100% certain)",
          words: ["definitely"],
          a: "'We're definitely going to hire'",
          tip: "Definitely = firm decision"
        },
        {
          type: "mc",
          q: "Exterior walls certainty level?",
          options: ["Might - 50% possible", "Will definitely - 100%", "Won't - 0%"],
          a: "Might - 50% possible",
          tip: "'Might...but not sure' = uncertain"
        }
      ]
    },

    // SET 3: Urban Problems Article
    {
      id: "m8-reading-s3",
      title: "Reading 3: City Life Challenges",
      category: "reading",
      questions: [
        {
          type: "passage",
          passage: `**Modern City Problems**\n\nUrban life can be difficult. Traffic jams are making the commute unbearable for workers. It's impossible to find parking in the downtown area during rush hour. The overcrowded trains make travel uncomfortable and sometimes unsafe for passengers.`
        },
        {
          type: "mc",
          q: "Finding parking downtown during rush hour is:",
          options: ["Impossible", "Easy", "Possible sometimes"],
          a: "Impossible",
          tip: "Text says 'impossible to find parking'"
        },
        {
          type: "input",
          q: `'Unbearable' means not ___. (negative prefix im- meaning)`,
          a: "bearable",
          tip: "Un- makes 'bearable' negative"
        },
        {
          type: "mc",
          q: "'Overcrowded' trains describe:",
          options: ["Too many people", "Empty trains", "Clean trains"],
          a: "Too many people",
          tip: "Over- prefix means 'too much'"
        },
        {
          type: "passage",
          passage: `**Transportation Issues**\n\nPublic transport has been facing problems for years. Buses are frequently delayed because of the road construction. The subway system is outdated and needs repairs. Many commuters have been complaining about the unreliable service and the disrespectful behavior of some passengers.`
        },
        {
          type: "order",
          q: "Service quality: 'The service is ___' (negative prefix un-)",
          words: ["unreliable"],
          a: "'The service is unreliable'",
          tip: "Un- + reliable = not reliable"
        },
        {
          type: "mc",
          q: "'Disrespectful' behavior means:",
          options: ["Not respectful", "Very respectful", "Somewhat respectful"],
          a: "Not respectful",
          tip: "Dis- makes 'respectful' negative"
        },
        {
          type: "input",
          q: `Passengers have been ___ for years. (present perfect continuous)`,
          a: "complaining",
          tip: "Have been + -ing for ongoing action"
        },
        {
          type: "passage",
          passage: `**Environmental Concerns**\n\nThe pollution in cities is alarming. Air quality has become unhealthy in many areas. It's irresponsible to ignore environmental problems. People need to use the public transportation system more. The government should make cars for individual use less attractive financially.`
        },
        {
          type: "mc",
          q: "Which uses 'the' correctly for specific things?",
          options: ["the pollution, the public transportation system", "pollution, public transportation system", "the pollution, public transportation"],
          a: "the pollution, the public transportation system",
          tip: "'The' for specific pollution and specific system"
        },
        {
          type: "input",
          q: `'Irresponsible' means not ___. (negative prefix ir-)`,
          a: "responsible",
          tip: "Ir- makes 'responsible' negative"
        },
        {
          type: "mc",
          q: "Air quality has become:",
          options: ["Unhealthy", "Healthy", "Better"],
          a: "Unhealthy",
          tip: "Un- + healthy = not healthy"
        },
        {
          type: "passage",
          passage: `**Future Solutions**\n\nThe city council is definitely going to invest in infrastructure next year. They'll probably build more bike lanes. They might introduce congestion charges, but citizens are uncertain about this. Officials think the traffic will improve, but residents doubt it will happen quickly.`
        },
        {
          type: "order",
          q: "Investment certainty: 'They're ___ going to invest' (100%)",
          words: ["definitely"],
          a: "'They're definitely going to invest'",
          tip: "Definitely = 100% certain plan"
        },
        {
          type: "mc",
          q: "Congestion charges certainty level?",
          options: ["Might - 50% uncertain", "Will definitely - 100%", "Won't - 0%"],
          a: "Might - 50% uncertain",
          tip: "'Might...uncertain' = 50% possibility"
        }
      ]
    },

    // SET 4: Career Planning Interview
    {
      id: "m8-reading-s4",
      title: "Reading 4: Future Career Plans",
      category: "reading",
      questions: [
        {
          type: "passage",
          passage: `**Interview with Recent Graduate Sarah**\n\nInterviewer: "What are your plans after graduation?"\n\nSarah: "I've been thinking about this for months! I'm definitely going to look for work in marketing. I've already sent out twenty applications. I hope I'm going to get interviews soon!"`
        },
        {
          type: "mc",
          q: "How long has Sarah been thinking about plans?",
          options: ["Months", "Days", "Years"],
          a: "Months",
          tip: "Text says 'for months'"
        },
        {
          type: "input",
          q: `Sarah has ___ sent applications. (modifier showing completion)`,
          a: "already",
          tip: "Already = completed action"
        },
        {
          type: "mc",
          q: "Sarah's job search certainty: 'I'm ___ going to look'",
          options: ["definitely - 100%", "probably - 75%", "might - 50%"],
          a: "definitely - 100%",
          tip: "Definitely = firm decision"
        },
        {
          type: "passage",
          passage: `**Career Preferences**\n\nInterviewer: "Why marketing?"\n\nSarah: "I enjoy working with people. I like creating campaigns and I love analyzing data. I can't stand doing repetitive tasks though. I want to learn new skills and I hope to become a manager eventually."`
        },
        {
          type: "order",
          q: "Preferences: 'I enjoy ___ with people' (gerund after enjoy)",
          words: ["working"],
          a: "'I enjoy working'",
          tip: "Enjoy + gerund (-ing form)"
        },
        {
          type: "mc",
          q: "'I want to learn' uses:",
          options: ["infinitive (to + base verb)", "gerund (-ing)", "past tense"],
          a: "infinitive (to + base verb)",
          tip: "Want + infinitive"
        },
        {
          type: "input",
          q: `'I can't stand ___' (gerund form of 'do')`,
          a: "doing",
          tip: "Can't stand + gerund"
        },
        {
          type: "passage",
          passage: `**Short-term Plans**\n\nInterviewer: "What about the next few months?"\n\nSarah: "I'm going to attend a networking event next week. I've just registered for it. I'll probably start applying to smaller companies too. I might take a short course to improve my digital skills."`
        },
        {
          type: "mc",
          q: "When is the networking event?",
          options: ["Next week", "Next month", "Tomorrow"],
          a: "Next week",
          tip: "Text says 'next week'"
        },
        {
          type: "order",
          q: "Recent action: 'I've ___ registered' (very recent)",
          words: ["just"],
          a: "'I've just registered'",
          tip: "Just = short time ago"
        },
        {
          type: "mc",
          q: "Course certainty level?",
          options: ["Might - 50% considering", "Will definitely - 100%", "Won't - 0%"],
          a: "Might - 50% considering",
          tip: "Might = uncertain possibility"
        },
        {
          type: "passage",
          passage: `**Long-term Vision**\n\nInterviewer: "Where do you see yourself in five years?"\n\nSarah: "I know I'm going to be successful because I'm determined. I think I'll probably be managing a team. I doubt I'll still be living in this city though. I might have moved abroad for better opportunities!"`
        },
        {
          type: "input",
          q: `'I ___ I'm going to be successful' (expresses certainty/knowledge)`,
          a: "know",
          tip: "Know = certain belief"
        },
        {
          type: "mc",
          q: "'I might have moved' refers to:",
          options: ["Past possibility at that future point", "Present action", "Certain future"],
          a: "Past possibility at that future point",
          tip: "Might have = past form looking back from future"
        }
      ]
    },

    // SET 5: Adventure Story
    {
      id: "m8-reading-s5",
      title: "Reading 5: Mountain Rescue",
      category: "reading",
      questions: [
        {
          type: "passage",
          passage: `**Day 1 - The Hike**\n\nJack and Emma had been hiking for three hours when they realized they were lost. "We might have taken the wrong path at the river," Jack said. Emma was worried. The weather was getting worse. It was absolutely freezing and a downpour had just started.`
        },
        {
          type: "mc",
          q: "How long had they been hiking?",
          options: ["Three hours", "One hour", "All day"],
          a: "Three hours",
          tip: "Text says 'for three hours'"
        },
        {
          type: "input",
          q: `'They ___ have taken wrong path' (past possibility - not sure)`,
          a: "might",
          tip: "Might have = uncertain past event"
        },
        {
          type: "mc",
          q: "Weather temperature was:",
          options: ["Freezing - extremely cold", "Mild - comfortable", "Boiling - very hot"],
          a: "Freezing - extremely cold",
          tip: "Freezing = extremely cold"
        },
        {
          type: "passage",
          passage: `**Day 1 - Evening**\n\n"We need to find shelter," Emma said. "I'll look for a cave while you gather firewood." They worked together to prepare themselves for the cold night. Jack had been learning survival skills, so he knew what to do. "We'll definitely survive this," he promised Emma.`
        },
        {
          type: "order",
          q: "Spontaneous offer: 'I ___ look for a cave' (deciding now)",
          words: ["'ll"],
          a: "'I'll look'",
          tip: "Will for instant decision/offer"
        },
        {
          type: "mc",
          q: "'Prepare themselves' is:",
          options: ["Reflexive pronoun - they prepared them", "Other people prepared them", "Past tense"],
          a: "Reflexive pronoun - they prepared them",
          tip: "Themselves = reflexive, subject = object"
        },
        {
          type: "input",
          q: `'We'll ___ survive' (100% promise/certainty)`,
          a: "definitely",
          tip: "Definitely = 100% certain promise"
        },
        {
          type: "passage",
          passage: `**Day 2 - Morning**\n\nThey woke up exhausted but alive. The rain had stopped but fog covered everything. "Search teams must have started looking for us by now," Emma said hopefully. "They might be searching the valley," Jack replied. "Or they could be miles away. We still haven't heard any helicopters yet."`
        },
        {
          type: "mc",
          q: "'Must have started' shows:",
          options: ["Strong deduction - 90% certain", "Uncertain guess - 50%", "Impossible - 0%"],
          a: "Strong deduction - 90% certain",
          tip: "Must have = logical deduction from evidence"
        },
        {
          type: "order",
          q: "Present possibility: 'They ___ be searching' (50% uncertain now)",
          words: ["might"],
          a: "'They might be searching'",
          tip: "Might be = uncertain present action"
        },
        {
          type: "input",
          q: `'We ___ haven't heard helicopters' (expected but not happened)`,
          a: "still",
          tip: "Still = ongoing situation, not yet changed"
        },
        {
          type: "passage",
          passage: `**Day 2 - Rescue**\n\nSuddenly, they heard voices! "That must be the rescue team!" Emma shouted. They were right. The team leader explained: "We've been searching for you since yesterday evening. You were impossible to find in the fog. We're definitely going to get you home safely now. Next time, you'll probably use a GPS, won't you?"`
        },
        {
          type: "mc",
          q: "Rescue team has been searching since:",
          options: ["Yesterday evening", "This morning", "Three days ago"],
          a: "Yesterday evening",
          tip: "Text says 'since yesterday evening'"
        },
        {
          type: "input",
          q: `'Impossible' to find = not ___. (negative prefix im-)`,
          a: "possible",
          tip: "Im- makes 'possible' negative"
        },
        {
          type: "mc",
          q: "'You'll probably use GPS' means:",
          options: ["75% likely future advice", "50% might use", "100% will definitely use"],
          a: "75% likely future advice",
          tip: "Will probably = 75% likelihood"
        }
      ]
    }
  ]
};
